<script lang="ts">
  import type { Pokemon, PokemonSpecies } from "../pokemon.model";
  import { createEventDispatcher, onMount } from "svelte";
  import { PokemonService } from "../pokemonService";
  import { decimeterToFeet, kgsToPound } from "../utility";
  import { pokeColor } from "../data/color";
  import { modalPokemonName } from "../stores";
  import Loader from "./Loader.svelte";

  export let modalPokemon: Pokemon;
  let pokemon: Pokemon;

  const dispatch = createEventDispatcher();
  let ps = new PokemonService();
  let isLoading = true;
  let stats = [];

  let bgColor = "white";
  let textColor = "#333";
  let rotateX = 0;
  let rotateY = 0;
  let simmerX = 50;
  let simmerY = 50;
  let opacity = 0;
  let containerElement: HTMLElement;

  function handleMouseMove(e: MouseEvent) {
    if (!containerElement) return;
    const rect = containerElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const pctX = (x / rect.width - 0.5) * 2;
    const pctY = (y / rect.height - 0.5) * 2;

    rotateY = pctX * 12;
    rotateX = -pctY * 12;

    simmerX = (x / rect.width) * 100;
    simmerY = (y / rect.height) * 100;
    opacity = 0.35;
  }

  function handleMouseLeave() {
    rotateX = 0;
    rotateY = 0;
    opacity = 0;
  }

  $: cssVarStyles = `--bg-color:${bgColor};--text-color:${textColor};--rotate-x:${rotateX}deg;--rotate-y:${rotateY}deg;--simmer-x:${simmerX}%;--simmer-y:${simmerY}%;--simmer-opacity:${opacity}`;

  onMount(() => {
    getDetailedData(modalPokemon);
  });

  function closeModal() {
    dispatch("modalClose", { close: true });
  }

  const sendFamily = (pokeName) => {
    dispatch("sendFamily", { name: pokeName });
  };

  modalPokemonName.subscribe((res: Pokemon) => {
    if (res && Object.keys(res).length > 0) {
      getDetailedData(res);
    }
  });

  function getDetailedData(data: Pokemon) {
    console.log(
      "🚀 ~ file: PokeModal.svelte ~ line 49 ~ getDetailedData ~ data",
      data,
    );

    pokemon = data;

    isLoading = true;
    ps.getPokemonSpecies(pokemon.species.url).then((res) => {
      if (res) {
        pokemon.speciesData = speciesAdapter(res);

        stats = pokemon.stats;

        ps.getEvolutionChain(pokemon.speciesData.evolution).then((res) => {
          if (res) {
            pokemon.evolution = [];
            getChain(res.chain);
            setColor(pokemon.speciesData.color);
            isLoading = false;
          }
        });
      }
    });
  }

  function setColor(color) {
    bgColor = pokeColor.find((ele) => ele.original === color).material;
  }

  function getChain(obj) {
    pokemon.evolution.push(obj.species);

    if (obj.evolves_to.length > 0) {
      getChain(obj.evolves_to[0]);
    }
  }

  const url = new URL("https://pokeapi.co/api/v2/pokemon-species/26/");

  function speciesAdapter(ele): PokemonSpecies {
    let pokemon: PokemonSpecies = {
      base_happiness: ele.base_happiness,
      color: ele.color.name,
      evolution: ele.evolution_chain.url,
      description: ele.flavor_text_entries
        .filter((text) => text.language.name === "en")
        .map((text) => text.flavor_text)[0],
      habitat: ele.habitat ? ele.habitat.name : " ",
      jp_name: ele.names
        .filter((name) => name.language.name === "ja")
        .map((name) => name.name)[0],
      genera: ele.genera
        .filter((genus) => genus.language.name === "en")
        .map((genus) => genus.genus)[0],
    };

    return pokemon;
  }

  function getAssetUrl() {
    let id = pokemon.id.toString().padStart(3, "0");

    return `https://assets.pokemon.com/assets/cms2/img/pokedex/full/${id}.png`;
  }

  function loadDefaultImage(event) {
    console.log(
      "🚀 ~ file: PokeModal.svelte ~ line 95 ~ loadDefaultImage ~ event",
      event,
    );
    event.src = pokemon.frontImage;
  }

  function getHp(pokemon: Pokemon): number {
    const hpStat = pokemon.stats?.find(s => s.name === "hp");
    return hpStat ? hpStat.base_stat * 2 : 120;
  }

  function getAttackDamage(pokemon: Pokemon, index: number): number {
    const atkStat = pokemon.stats?.find(s => s.name === "attack");
    const baseAtk = atkStat ? atkStat.base_stat : 60;
    return index === 0 ? Math.round(baseAtk * 0.8) : Math.round(baseAtk * 1.3);
  }

  function getPrevEvolution(pokemon: Pokemon): string | null {
    if (!pokemon.evolution || pokemon.evolution.length < 2) return null;
    const index = pokemon.evolution.findIndex(e => e.name.toLowerCase() === pokemon.name.toLowerCase());
    if (index > 0) {
      return pokemon.evolution[index - 1].name;
    }
    return null;
  }

  function getWeakness(type: string): string {
    const weaknesses: Record<string, string> = {
      fire: "water",
      water: "grass",
      grass: "fire",
      electric: "ground",
      ground: "water",
      rock: "water",
      ice: "fire",
      fighting: "psychic",
      psychic: "ghost",
      ghost: "dark",
      dark: "fighting",
      steel: "fire",
      dragon: "fairy",
      fairy: "steel",
      bug: "fire",
      poison: "ground",
      flying: "electric",
      normal: "fighting",
    };
    return weaknesses[type.toLowerCase()] || "colorless";
  }

  function getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      fire: "#ff4422",
      water: "#3399ff",
      grass: "#77cc55",
      electric: "#ffcc00",
      ground: "#ddb088",
      rock: "#bbaa66",
      ice: "#66ccff",
      fighting: "#bb5544",
      psychic: "#ff5599",
      ghost: "#775599",
      dark: "#705848",
      steel: "#aaaabb",
      dragon: "#7766ee",
      fairy: "#ee99ee",
      bug: "#aabb22",
      poison: "#aa5599",
      flying: "#8899ff",
      normal: "#aaaa99",
    };
    return colors[type.toLowerCase()] || "#aaaaaa";
  }
</script>

<div class="modal-screen">
  {#if !isLoading}
    <div
      class="modal-container"
      style={cssVarStyles}
      bind:this={containerElement}
      on:mousemove={handleMouseMove}
      on:mouseleave={handleMouseLeave}
    >
      <div class="close-btn" on:click={() => closeModal()}>✘</div>

      <!-- TCG Top Bar -->
      <div class="tcg-header">
        <div class="tcg-title-block">
          <span class="tcg-stage">Basic Pokémon</span>
          <span class="tcg-name">{pokemon.name}</span>
        </div>
        <div class="tcg-hp-block">
          <span class="tcg-hp-text">HP</span>
          <span class="tcg-hp-val">{getHp(pokemon)}</span>
          <span class="tcg-energy-circle header-energy" style="background-color: {getTypeColor(pokemon.types[0])}">
            {pokemon.types[0][0].toUpperCase()}
          </span>
        </div>
      </div>

      <!-- TCG Sub-header Evolution Bar -->
      <div class="tcg-subheader">
        {#if getPrevEvolution(pokemon)}
          Evolves from {getPrevEvolution(pokemon)} • JP: {pokemon.speciesData.jp_name}
        {:else}
          Basic Pokémon • JP: {pokemon.speciesData.jp_name}
        {/if}
      </div>

      <!-- TCG Holo Image Frame -->
      <div class="tcg-art-window">
        <div class="avatar">
          <img
            src={getAssetUrl()}
            alt={pokemon.name}
            on:error={loadDefaultImage}
          />
        </div>
      </div>

      <!-- TCG Specs strip -->
      <div class="tcg-specs-strip">
        NO. {pokemon.id} {pokemon.speciesData.genera} HT: {pokemon.height / 10} m WT: {pokemon.weight / 10} kg
      </div>

      <!-- TCG Attacks and Abilities Section -->
      <div class="tcg-moves-section">
        <!-- Ability -->
        {#if pokemon.abilities && pokemon.abilities.length > 0}
          <div class="tcg-ability">
            <span class="tcg-ability-badge">Ability: {pokemon.abilities[0]}</span>
            <p class="tcg-ability-desc">This Pokémon has the extraordinary ability {pokemon.abilities[0]}.</p>
          </div>
        {/if}

        <!-- Attack 1 -->
        <div class="tcg-attack-row">
          <div class="tcg-attack-cost">
            <span class="tcg-energy-circle" style="background-color: {getTypeColor(pokemon.types[0])}"></span>
            <span class="tcg-energy-circle" style="background: #aab"></span>
          </div>
          <div class="tcg-attack-info">
            <span class="tcg-attack-name">{pokemon.moves && pokemon.moves[0] ? pokemon.moves[0] : 'Tackle'}</span>
            <p class="tcg-attack-desc">Flips a coin. If heads, this attack does extra damage.</p>
          </div>
          <span class="tcg-attack-damage">{getAttackDamage(pokemon, 0)}</span>
        </div>

        <!-- Attack 2 -->
        {#if pokemon.moves && pokemon.moves[1]}
          <div class="tcg-attack-row border-top">
            <div class="tcg-attack-cost">
              <span class="tcg-energy-circle" style="background-color: {getTypeColor(pokemon.types[0])}"></span>
              <span class="tcg-energy-circle" style="background-color: {getTypeColor(pokemon.types[0])}"></span>
            </div>
            <div class="tcg-attack-info">
              <span class="tcg-attack-name">{pokemon.moves[1]}</span>
              <p class="tcg-attack-desc">Discard 1 energy from this Pokémon to deal massive damage.</p>
            </div>
            <span class="tcg-attack-damage">{getAttackDamage(pokemon, 1)}</span>
          </div>
        {/if}
      </div>

      <!-- TCG Card Bottom Stats & Weaknesses -->
      <div class="tcg-footer">
        <div class="tcg-footer-item">
          <span class="tcg-footer-label">weakness</span>
          <span class="tcg-energy-circle small" style="background-color: {getTypeColor(getWeakness(pokemon.types[0]))}"></span>
        </div>
        <div class="tcg-footer-item">
          <span class="tcg-footer-label">resistance</span>
          <span class="tcg-resistance-text">-30</span>
        </div>
        <div class="tcg-footer-item">
          <span class="tcg-footer-label">retreat</span>
          <div class="tcg-retreat-dots">
            <span class="tcg-energy-circle small" style="background: #aab"></span>
          </div>
        </div>
      </div>

      <!-- Card Lore Description -->
      <div class="tcg-bottom-desc">
        {pokemon.speciesData.description.replace(/\f/g, " ")}
      </div>

      <!-- Evolution Chain / Footer bar -->
      <div class="tcg-evolution-chain">
        <span class="tcg-evo-label">Evolution:</span>
        <div class="family">
          {#each pokemon.evolution as name}
            <div class="link" on:click={() => sendFamily(name.name)}>
              <span>{name.name}</span>
            </div>
          {/each}
        </div>
      </div>

      <div class="tcg-card-number">
        {pokemon.id}/898 ★
      </div>
    </div>
  {/if}

  {#if isLoading}
    <div class="modal-container" style={cssVarStyles}>
      <Loader />
    </div>
  {/if}
</div>

<style>
  .modal-screen {
    z-index: 200;
    width: 100vw;
    height: 100vh;
    background-color: rgba(96, 128, 160, 0.74);
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0;
    right: 0;
  }

  .modal-container {
    z-index: 250;
    width: 30rem;
    max-height: 800px;
    border-radius: 16px;
    padding: 2rem;
    color: var(--text-color, #333);
    font-size: 1.2rem;

    /* Metallic Golden Gradient Border */
    border: 8px solid transparent;
    background-image: linear-gradient(var(--bg-color, white), var(--bg-color, white)), 
                      linear-gradient(135deg, #ffe066, #f5af19, #e65c00, #ffd700, #b8860b, #ffe066);
    background-origin: border-box;
    background-clip: padding-box, border-box;

    /* 3D tilt and golden glow effects */
    transform: perspective(1000px) rotateX(var(--rotate-x, 0deg)) rotateY(var(--rotate-y, 0deg));
    transition: transform 0.1s ease-out, box-shadow 0.1s ease-out;
    box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2),
                0 0 15px rgba(245, 175, 25, 0.35);
    transform-style: preserve-3d;
    position: relative;
    overflow: hidden;
  }

  .modal-container:hover {
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3),
                0 0 30px rgba(245, 175, 25, 0.55);
  }

  /* Holo / Simmer Sheen effect */
  .modal-container::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at var(--simmer-x, 50%) var(--simmer-y, 50%),
      rgba(255, 255, 255, var(--simmer-opacity, 0)) 0%,
      rgba(255, 255, 255, 0) 50%
    ), linear-gradient(
      115deg,
      transparent 0%,
      rgba(255, 255, 255, calc(var(--simmer-opacity, 0) * 0.45)) calc(var(--simmer-x, 50%) - 15%),
      transparent calc(var(--simmer-x, 50%) + 15%),
      transparent 100%
    );
    pointer-events: none;
    z-index: 300;
    mix-blend-mode: color-dodge;
  }


  .close-btn {
    float: right;
    font-size: 1.8rem;
    margin-top: -15px;
    margin-right: -10px;
    cursor: pointer;
    z-index: 400;
    position: relative;
    color: var(--text-color, #333);
  }

  /* TCG Top Header */
  .tcg-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid rgba(0, 0, 0, 0.15);
    padding-bottom: 4px;
  }

  .tcg-title-block {
    display: flex;
    flex-direction: column;
  }

  .tcg-stage {
    font-size: 0.75rem;
    font-style: italic;
    font-weight: 600;
    color: #444;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .tcg-name {
    font-size: 1.8rem;
    font-weight: 800;
    text-transform: capitalize;
    font-family: 'Outfit', 'Arial Black', sans-serif;
    letter-spacing: -0.5px;
    text-shadow: 1px 1px 0px rgba(255, 255, 255, 0.5);
  }

  .tcg-hp-block {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .tcg-hp-text {
    font-size: 0.8rem;
    font-weight: 800;
    color: #ff3333;
  }

  .tcg-hp-val {
    font-size: 1.6rem;
    font-weight: 900;
    color: #ff3333;
    font-family: 'Impact', sans-serif;
  }

  .tcg-energy-circle {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: white;
    font-weight: bold;
    font-size: 1rem;
    box-shadow: inset 0 -2px 4px rgba(0, 0, 0, 0.4), 
                0 2px 4px rgba(0, 0, 0, 0.2);
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
  }

  .tcg-energy-circle.header-energy {
    width: 32px;
    height: 32px;
    font-size: 1.1rem;
    margin-left: 8px;
  }

  .tcg-energy-circle.small {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    box-shadow: inset 0 -1px 2px rgba(0, 0, 0, 0.4);
  }

  /* Subheader Evolution Bar */
  .tcg-subheader {
    font-size: 0.75rem;
    font-style: italic;
    background: linear-gradient(90deg, #ffd700, #ffe066, #d4af37);
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    padding: 2px 8px;
    margin-top: 4px;
    font-weight: 600;
    color: #333;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  /* Holo Art Window Frame */
  .tcg-art-window {
    background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(200, 220, 240, 0.9) 100%);
    border: 4px solid #d4af37;
    border-radius: 6px;
    margin-top: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1),
                inset 0 0 20px rgba(255,255,255,0.6);
    overflow: hidden;
  }

  .avatar {
    transform: translateZ(30px);
    transition: transform 0.1s ease-out;
  }

  .avatar img {
    width: 13rem;
    height: 13rem;
    filter: drop-shadow(0 8px 16px rgba(0,0,0,0.15));
  }

  /* Specs Strip */
  .tcg-specs-strip {
    font-size: 0.7rem;
    font-style: italic;
    text-align: center;
    background: linear-gradient(90deg, #e3c053, #f7df8f, #e3c053);
    border: 1px solid rgba(0, 0, 0, 0.15);
    padding: 2px;
    font-weight: 700;
    color: #333;
    margin-top: 4px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.4);
  }

  /* Moves and Attacks List */
  .tcg-moves-section {
    margin-top: 10px;
    background: rgba(255, 255, 255, 0.45);
    border-radius: 8px;
    padding: 8px 12px;
    border: 1px solid rgba(0,0,0,0.05);
  }

  /* Ability */
  .tcg-ability {
    margin-bottom: 8px;
    border-bottom: 1px dashed rgba(0,0,0,0.15);
    padding-bottom: 6px;
  }

  .tcg-ability-badge {
    font-size: 0.8rem;
    font-weight: 800;
    color: #cc2200;
    text-transform: uppercase;
    background: rgba(200,30,0,0.1);
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid rgba(200,30,0,0.2);
    display: inline-block;
  }

  .tcg-ability-desc {
    font-size: 0.85rem;
    margin: 4px 0 0 0;
    line-height: 1.15rem;
    color: #444;
    font-style: italic;
  }

  /* Attack Row */
  .tcg-attack-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    gap: 12px;
  }

  .tcg-attack-row.border-top {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .tcg-attack-cost {
    display: flex;
    gap: 3px;
    width: 48px;
    flex-shrink: 0;
  }

  .tcg-attack-info {
    flex-grow: 1;
  }

  .tcg-attack-name {
    font-size: 1rem;
    font-weight: 800;
    text-transform: capitalize;
    color: #111;
  }

  .tcg-attack-desc {
    font-size: 0.8rem;
    margin: 2px 0 0 0;
    color: #555;
    line-height: 1.1rem;
  }

  .tcg-attack-damage {
    font-size: 1.4rem;
    font-weight: 900;
    color: #111;
    font-family: 'Impact', 'Arial Black', sans-serif;
    width: 32px;
    text-align: right;
  }

  /* TCG Bottom Footer info */
  .tcg-footer {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
    padding-top: 6px;
  }

  .tcg-footer-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .tcg-footer-label {
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    color: #666;
  }

  .tcg-resistance-text {
    font-size: 0.75rem;
    font-weight: 800;
    color: #333;
  }

  .tcg-retreat-dots {
    display: flex;
    gap: 2px;
  }

  /* Lore description */
  .tcg-bottom-desc {
    font-size: 0.8rem;
    font-style: italic;
    line-height: 1.25rem;
    color: #333;
    margin-top: 10px;
    border: 1px solid rgba(0,0,0,0.1);
    background: rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    padding: 6px 10px;
  }

  /* Evolution Chain bottom */
  .tcg-evolution-chain {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    font-size: 0.85rem;
  }

  .tcg-evo-label {
    font-weight: 800;
    color: #444;
  }

  .family {
    text-transform: capitalize;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .family .link + .link:before {
    content: "► ";
    margin-right: 4px;
    color: #d4af37;
  }

  .link {
    display: inline;
    cursor: pointer;
    color: #3c3399;
    transition: color 0.15s ease;
  }

  .link:hover {
    color: #ff3333;
    text-decoration: underline;
  }

  .tcg-card-number {
    text-align: right;
    font-size: 0.7rem;
    font-weight: bold;
    color: #666;
    margin-top: 6px;
  }
</style>
